/* eslint-disable global-require */
import React from 'react';
import PropTypes from 'prop-types';
import Link from 'gatsby-link';
import { css, cx } from 'emotion';
import Color from 'color';

import presets, { colors, baselineGrid, buttonReset } from '../utils/presets';
import { options, rhythm } from '../utils/typography';
import { toRem } from '../utils/functions';

import Arrow from '../assets/svg-sprite/shape-button-arrow.svg';

const { speed, curve } = presets.animation;

const Button = ({
  color,
  backgroundColor,
  borderColor,
  to,
  children,
  component,
  type,
  disabled,
  hollow,
  shadow,
  gradient,
  arrow,
  large,
  className,
  ...other
}) => {
  const internal = /^\/(?!\/)/.test(to);

  const buttonProps = {};

  let ComponentProp = component;

  if (ComponentProp === 'button' && internal) {
    ComponentProp = Link;
    buttonProps.to = to;
  } else if (ComponentProp === 'button' && to) {
    ComponentProp = 'a';
    buttonProps.href = to;
    buttonProps.target = '_blank';
  }

  if (ComponentProp === 'button') {
    buttonProps.type = type || 'button';
    buttonProps.disabled = disabled;
  } else {
    buttonProps.role = 'button';
  }

  const lineHeight = 18;
  const buttonTextColor = Color(backgroundColor).isLight() ? colors.gray.dark : '#fff';

  const button = css`
    ${buttonReset};
    display: inline-block;
    margin-bottom: ${rhythm(options.blockMarginBottom)};
    padding: ${toRem(baselineGrid * 1.5)} ${toRem(baselineGrid * 3)};
    font-size: ${toRem(16)};
    font-weight: 600;
    text-align: center;
    line-height: ${toRem(lineHeight)};
    text-decoration: none;
    color: ${color || buttonTextColor};
    border-radius: ${toRem(baselineGrid * 4)};
    background-color: ${backgroundColor};
    transition: opacity ${speed.default} ${curve.default},
      transform ${speed.default} ${curve.default};
    cursor: pointer;
    user-select: none;
    opacity: 1;
    transform: none;

    &:hover,
    &:active,
    &:focus {
      opacity: 0.8;
      transform: translate(0, -2px);
    }

    > * {
      display: inline-block;
      vertical-align: top;
    }

    svg {
      height: ${toRem(lineHeight)};
      fill: currentColor;

      &:not(.arrow) {
        margin-left: ${toRem(-2)};
        margin-right: ${toRem(baselineGrid)};
        transform: scale(1.2);
      }

      &.arrow {
        margin-left: ${toRem(baselineGrid * 0.5)};
        margin-right: ${toRem(baselineGrid * -0.5)};
        transform: translate(0, 1px);
      }
    }
  `;

  /* Larger button */
  const largeButton = css`
    padding: ${toRem(baselineGrid * 2.5)} ${toRem(baselineGrid * 4)};
    font-size: ${toRem(18)};
    border-radius: ${toRem(baselineGrid * 5)};
  `;

  /* Hollow button */
  const hollowBorderColor = borderColor || backgroundColor;
  const hollowButton = css`
    background: transparent;
    color: inherit;
    box-shadow: inset 0 0 0 3px ${hollowBorderColor};
  `;

  /* Gradient button */
  let gradientColor = Color(backgroundColor);
  if (gradient) {
    const { hue, rotate, saturation, lightness, lighten } = gradient;
    if (Object.prototype.hasOwnProperty.call(gradient, 'hue')) {
      gradientColor = gradientColor.hue(hue);
    } else if (Object.prototype.hasOwnProperty.call(gradient, 'rotate')) {
      gradientColor = gradientColor.rotate(rotate);
    } else {
      gradientColor = gradientColor.rotate(gradientColor.hue() > 180 ? -32 : 32);
    }
    if (Object.prototype.hasOwnProperty.call(gradient, 'saturation')) {
      gradientColor = gradientColor.saturationl(saturation);
    }
    if (Object.prototype.hasOwnProperty.call(gradient, 'lightness')) {
      gradientColor = gradientColor.lightness(lightness);
    } else if (Object.prototype.hasOwnProperty.call(gradient, 'lighten')) {
      gradientColor = gradientColor.lighten(lighten);
    }
  }
  const gradientButton = css`
    background: linear-gradient(to right, ${backgroundColor}, ${gradientColor.string()});
  `;

  /* Shadow button */
  const shadowButton = css`
    transition: box-shadow ${speed.default} ${curve.default},
      opacity ${speed.default} ${curve.default}, transform ${speed.default} ${curve.default};
    box-shadow: 0 8px 32px -8px ${Color(backgroundColor)
          .alpha(0.64)
          .string()},
      0 1px 2px rgba(0, 0, 0, 0.16);

    &:hover {
      box-shadow: 0 10px 32px -8px ${Color(backgroundColor)
            .alpha(0.64)
            .string()},
        0 1px 2px rgba(0, 0, 0, 0.16);
    }
  `;

  return (
    <ComponentProp
      className={cx(button, className, {
        [largeButton]: large,
        [hollowButton]: hollow,
        [shadowButton]: shadow && !hollow,
        [gradientButton]: gradient && !hollow,
      })}
      {...buttonProps}
      {...other}
    >
      {children}
      {arrow && (
        <svg className={'arrow'} viewBox={Arrow.viewBox}>
          <use xlinkHref={`#${Arrow.id}`} />
        </svg>
      )}
    </ComponentProp>
  );
};

Button.propTypes = {
  color: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  type: PropTypes.string,
  to: PropTypes.string,
  children: PropTypes.node,
  disabled: PropTypes.bool,
  hollow: PropTypes.bool,
  shadow: PropTypes.bool,
  gradient: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  arrow: PropTypes.bool,
  large: PropTypes.bool,
  className: PropTypes.string,
  other: PropTypes.array,
};

Button.defaultProps = {
  backgroundColor: colors.pixelmoxie,
  component: 'button',
  type: 'button',
};

export default Button;



// WEBPACK FOOTER //
// ./src/components/button.js